import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ServiceRepository } from '../../domain/repositories/service.repository';
import { Logger } from 'src/app/core/interfaces/Logger';
import { IService } from 'src/app/core/interfaces/service.interface';
import { ServicesStorageService } from '../../services-storage.service';

@Injectable({
  providedIn: 'root',
})
export class ServiceRepositoryAdapter implements ServiceRepository, OnDestroy {
  private readonly servicesStorageService = inject(ServicesStorageService);
  private readonly logger = inject(Logger);

  private readonly onServiceFinished = new Subject<void>();
  private readonly onServiceStarted = new Subject<void>();

  readonly onServiceFinished$ = this.onServiceFinished.asObservable();
  readonly onServiceStarted$ = this.onServiceStarted.asObservable();

  /**
   * Notifies that a service has finished
   */
  notifyOnServiceFinished(): void {
    this.onServiceFinished.next();
  }

  /**
   * Notifies that a service has started
   */
  notifyOnServiceStarted(): void {
    this.onServiceStarted.next();
  }

  /**
   * Gets the current service from cache
   * @returns {IService | null} The current service
   */
  getCurrentServiceFromCache(): IService | null {
    return this.servicesStorageService.getServiceFromCache().getValue();
  }

  /**
   * Gets the current service from IndexedDB
   * @returns {Observable<IService | null>} An observable of the current service
   */
  getCurrentService(): Observable<IService | null> {
    return this.servicesStorageService.getCurrentService();
  }

  /**
   * Gets the current service from the server
   * @returns {Observable<IService | null>} An observable of the current service
   */
  getServiceFromServer(): Observable<IService | null> {
    return this.servicesStorageService.getServiceFromServer();
  }

  /**
   * Inserts a service into IndexedDB
   * @param {IService} service The service to insert
   * @returns {Observable<IService | null>} An observable of the inserted service
   */
  insertService(service: IService): Observable<IService | null> {
    return this.servicesStorageService.insertService(service);
  }

  /**
   * Removes the current service from IndexedDB
   */
  removeService(): void {
    this.servicesStorageService.removeService();
  }

  /**
   * Cleanup resources when service is destroyed
   * Complete all Subjects to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.logger.debug('[ServicesService] Cleaning up resources');
    
    if (this.onServiceFinished) {
      this.onServiceFinished.complete();
    }
    
    if (this.onServiceStarted) {
      this.onServiceStarted.complete();
    }
  }
}
